import { Button } from 'components';
import { usePartnerAdmin } from 'hooks/usePartnersAdmin';
import { useDisclose } from 'hooks/utilHooks';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { IPartner, ThemeStatus } from 'types/Partner';
import { maskCNPJ } from 'util/masks';

import CloseIcon from '@mui/icons-material/Close';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { Checkbox } from 'components/Inputs/Checkbox';
import { FormProvider, useForm } from 'react-hook-form';
import { useEffect, useMemo } from 'react';

export interface IPartnerRow {
  partner: IPartner;
}

export const PartnerRow: React.FC<IPartnerRow> = ({ partner }) => {
  let history = useHistory();
  let { path } = useRouteMatch();
  const { isOpen, onClose, onOpen } = useDisclose();

  const form = useForm({ defaultValues: { exclude: false } });
  const exclude = form.watch('exclude');

  const { deleteItem, loadingDelete } = usePartnerAdmin();

  const handleEdit = () => {
    history.push(`${path}/edit/${partner._id}`);
  };

  const handleView = () => {
    history.push(`${path}/view/${partner._id}`);
  };

  useEffect(() => {
    form.reset();
  }, [form, partner]);

  const { company, cnpj, status, active, responsible } = partner;

  //@ts-ignore
  const themeStatus = ThemeStatus[status];

  const { color, title } = useMemo(() => {
    if (!active || (status === 'pending' && !company)) {
      return { color: '#d9bf16', title: 'Pré-cadastro' };
    }

    return themeStatus;
  }, [active, status, company, themeStatus]);

  return (
    <>
      <TableRow key={partner.socialReason} hover>
        <TableCell component="th" scope="row">
          {company ?? '-'}
        </TableCell>

        <TableCell>{cnpj ? maskCNPJ(cnpj) : '-'}</TableCell>

        <TableCell>{responsible ? responsible : '-'}</TableCell>

        <TableCell>
          <Typography color={color ?? '#000'}>{title}</Typography>
        </TableCell>

        <TableCell>
          <Stack flexDirection="row" gap="8px">
            <IconButton aria-label="edit" color="inherit" onClick={handleEdit}>
              <CreateIcon />
            </IconButton>

            <IconButton aria-label="view" color="inherit" onClick={handleView}>
              <SearchIcon />
            </IconButton>

            <IconButton aria-label="delete" color="inherit" onClick={onOpen}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>

      <Dialog open={isOpen} onClose={onClose}>
        <DialogTitle>
          Exclusão permanente do parceiro
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pb: 0 }}>
          <DialogContentText>
            <Grid container spacing={2}>
              <Grid item>
                Essa ação é permanente, você deseja continuar e excluir todos os
                dados desse parceiro?
              </Grid>
              <Grid item>
                <FormProvider {...form}>
                  <Checkbox
                    name="exclude"
                    id="exclude"
                    label="Eu concordo em excluir permanente esse parceiro."
                    labelPlacement="end"
                  />
                </FormProvider>
              </Grid>
            </Grid>
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            variant="text"
            onClick={() => {
              onClose();
              form.reset();
            }}
          >
            Não
          </Button>

          <Button
            variant="text"
            disabled={!exclude || loadingDelete.loading}
            onClick={() => {
              deleteItem(partner._id);
              onClose();
            }}
          >
            Sim
            {loadingDelete.loading && (
              <CircularProgress
                size="small"
                sx={{ width: 24, height: 24, ml: 2 }}
              />
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
